/** Date filter component with preset buttons and custom date picker. */
import React from "react";
import {
  Box,
  Button,
  ButtonGroup,
  TextField,
  Typography,
} from "@mui/material";
import { format, startOfDay, endOfDay, subDays, startOfMonth } from "date-fns";

interface DateFilterProps {
  startDate: Date;
  endDate: Date;
  onDateChange: (startDate: Date, endDate: Date) => void;
}

/** Date preset options. */
type DatePreset =
  | "today"
  | "yesterday"
  | "last7Days"
  | "last30Days"
  | "thisMonth"
  | "custom";

/** Get date range for a preset. */
function getDateRange(preset: DatePreset): { start: Date; end: Date } {
  const now = new Date();

  switch (preset) {
    case "today":
      return {
        start: startOfDay(now),
        end: endOfDay(now),
      };
    case "yesterday":
      return {
        start: startOfDay(subDays(now, 1)),
        end: endOfDay(subDays(now, 1)),
      };
    case "last7Days":
      return {
        start: startOfDay(subDays(now, 7)),
        end: now,
      };
    case "last30Days":
      return {
        start: startOfDay(subDays(now, 30)),
        end: now,
      };
    case "thisMonth":
      return {
        start: startOfMonth(now),
        end: now,
      };
    case "custom":
    default:
      return {
        start: now,
        end: now,
      };
  }
}

/** Determine current preset from dates. */
function getCurrentPreset(startDate: Date, endDate: Date): DatePreset {
  const now = new Date();
  const today = {
    start: startOfDay(now),
    end: endOfDay(now),
  };
  const yesterday = {
    start: startOfDay(subDays(now, 1)),
    end: endOfDay(subDays(now, 1)),
  };

  // Check today
  if (
    startDate.getTime() === today.start.getTime() &&
    endDate.getTime() === today.end.getTime()
  ) {
    return "today";
  }

  // Check yesterday
  if (
    startDate.getTime() === yesterday.start.getTime() &&
    endDate.getTime() === yesterday.end.getTime()
  ) {
    return "yesterday";
  }

  // Check last 7 days (approximate)
  const last7Start = startOfDay(subDays(now, 7));
  if (
    startDate.getTime() === last7Start.getTime() &&
    endDate >= subDays(now, 1)
  ) {
    return "last7Days";
  }

  // Check this month
  const thisMonthStart = startOfMonth(now);
  if (startDate.getTime() === thisMonthStart.getTime()) {
    return "thisMonth";
  }

  return "custom";
}

/** Date filter component with presets and custom date range picker. */
export default function DateFilter({
  startDate,
  endDate,
  onDateChange,
}: DateFilterProps) {
  const [currentPreset, setCurrentPreset] = React.useState<DatePreset>(
    getCurrentPreset(startDate, endDate)
  );

  const handlePresetClick = (preset: DatePreset) => {
    setCurrentPreset(preset);
    if (preset !== "custom") {
      const { start, end } = getDateRange(preset);
      onDateChange(start, end);
    }
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = new Date(event.target.value);
    if (!isNaN(newStartDate.getTime())) {
      setCurrentPreset("custom");
      onDateChange(newStartDate, endDate);
    }
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = new Date(event.target.value);
    if (!isNaN(newEndDate.getTime())) {
      setCurrentPreset("custom");
      onDateChange(startDate, newEndDate);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: 2,
        alignItems: { xs: "flex-start", sm: "center" },
        mb: 3,
      }}
    >
      <Typography variant="body2" sx={{ minWidth: 80 }}>
        Date Range:
      </Typography>

      {/* Preset buttons */}
      <ButtonGroup size="small">
        <Button
          variant={currentPreset === "today" ? "contained" : "outlined"}
          onClick={() => handlePresetClick("today")}
        >
          Today
        </Button>
        <Button
          variant={currentPreset === "yesterday" ? "contained" : "outlined"}
          onClick={() => handlePresetClick("yesterday")}
        >
          Yesterday
        </Button>
        <Button
          variant={currentPreset === "last7Days" ? "contained" : "outlined"}
          onClick={() => handlePresetClick("last7Days")}
        >
          Last 7 Days
        </Button>
        <Button
          variant={currentPreset === "last30Days" ? "contained" : "outlined"}
          onClick={() => handlePresetClick("last30Days")}
        >
          Last 30 Days
        </Button>
        <Button
          variant={currentPreset === "thisMonth" ? "contained" : "outlined"}
          onClick={() => handlePresetClick("thisMonth")}
        >
          This Month
        </Button>
      </ButtonGroup>

      {/* Custom date inputs */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          label="From"
          type="datetime-local"
          size="small"
          value={format(startDate, "yyyy-MM-dd'T'HH:mm")}
          onChange={handleStartDateChange}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 200 }}
        />
        <Typography variant="body2">to</Typography>
        <TextField
          label="To"
          type="datetime-local"
          size="small"
          value={format(endDate, "yyyy-MM-dd'T'HH:mm")}
          onChange={handleEndDateChange}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 200 }}
        />
      </Box>
    </Box>
  );
}
